import axios from 'axios';
import { getApiBaseUrl } from './defines/apiUrl';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY ?? '';
const API_BASE = getApiBaseUrl();

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from(rawData.split('').map(c => c.charCodeAt(0)));
}

/** Status atual da permissão de push */
export function getPushPermissionStatus(): NotificationPermission | 'unsupported' {
    if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        return 'unsupported';
    }
    return Notification.permission;
}

/** Registra o service worker e faz subscribe na Web Push API */
async function subscribeAndRegister(token: string): Promise<boolean> {
    if (!VAPID_PUBLIC_KEY) {
        console.warn('[push] VAPID public key não configurada');
        return false;
    }

    const registration = await navigator.serviceWorker.register('/sw.js');
    await navigator.serviceWorker.ready;

    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
    }

    const sub = subscription.toJSON();
    await axios.post(
        `${API_BASE}/push-register`,
        {
            token,
            endpoint: sub.endpoint,
            auth:     sub.keys?.auth     ?? '',
            p256dh:   sub.keys?.p256dh   ?? '',
        },
        { withCredentials: true }
    );

    return true;
}

/**
 * Solicita permissão (se necessário) e registra a subscrição push.
 * @param token  JWT da usina para identificar para qual usina esta subscrição se aplica
 * @returns `'granted'` se ativado, `'denied'` se bloqueado, `'unsupported'` se browser não suporta
 */
export async function requestAndRegisterPush(token: string): Promise<'granted' | 'denied' | 'unsupported'> {
    const status = getPushPermissionStatus();
    if (status === 'unsupported') return 'unsupported';
    if (status === 'denied') return 'denied';

    if (status !== 'granted') {
        const result = await Notification.requestPermission();
        if (result !== 'granted') return 'denied';
    }

    try {
        await subscribeAndRegister(token);
        return 'granted';
    } catch (e) {
        console.error('[push] Erro ao registrar subscrição:', e);
        return 'denied';
    }
}

/**
 * Verifica se já existe uma subscrição ativa (sem pedir permissão).
 */
export async function checkExistingPushSubscription(): Promise<boolean> {
    if (getPushPermissionStatus() !== 'granted') return false;
    try {
        const reg = await navigator.serviceWorker.getRegistration('/sw.js');
        if (!reg) return false;
        const sub = await reg.pushManager.getSubscription();
        return !!sub;
    } catch {
        return false;
    }
}
